<script>
  import {combineLatest, merge} from 'rxjs';
  import {filter, map, switchMap} from 'rxjs/operators';
  import {getContext, onMount} from 'svelte';
  import {stringify} from 'query-string';
  import {goto} from '@sapper/app';
  import {LINKS} from '@/const/link';
  import {PATHS} from '@/const/paths';
  import {mc} from '@/core';
  import {envUtil} from '@/helper/env';
  import {bridge} from '@/helper/bridge';
  import {drawerUtil} from '@/helper/drawer';
  import {popupUtil} from '@/helper/popup';
  import {hasUsedUID} from '@/storage/uids';
  import {userInfo$} from '@/store/user';
  import {fixLink$} from '@/store/lang';
  import {logout$} from '@/streams/auth';
  import Button from '@/widgets/Button.svelte';
  import {TRADING_GAME} from '../images';
  import {ERROR_CODE} from '../const';
  import {joinNow$, joinSuccess$, joinFail$, myEntryInfo$, gameStatus$, myEntryInfoLoaded$} from '../stream';
  import JoinFailedDialog from '../popups/JoinFailedDialog.svelte';
  import lang from '../TradeCompetitionPage.lang';

  const LG = getContext('LG');
  const shouldDisplayJoinBtn$ = combineLatest(gameStatus$, myEntryInfo$).pipe(
    map(([gameStatus, myEntryInfo]) => {
      return gameStatus.initialed && !myEntryInfo.isJoinedCompetition && !gameStatus.isBeforeSignUpStart && !gameStatus.isCompetitionEnd;
    })
  );
  const STORAGE_KEY = 'TRADE_COMPETITION_AUTO_JOIN';

  let bannerWidth = 1200;

  onMount(() => {
    const automaticJoinSubscription = myEntryInfoLoaded$
      .pipe(
        switchMap(() => shouldDisplayJoinBtn$),
        filter((val) => val)
      )
      .subscribe(() => automaticJoinIfNecessary());
    const clearSubscription = merge(myEntryInfoLoaded$, logout$).subscribe(() => {
      setTimeout(() => {
        localStorage.removeItem(STORAGE_KEY);
      });
    });
    // '加入成功' 弹窗
    const successSubscription = joinSuccess$.subscribe(() => popupUtil.create(JoinFailedDialog, {errorCode: ERROR_CODE.SUCCESS}));
    const failSubscription = joinFail$.subscribe((error) => {
      // 此时弹出 '子账户不允许参赛' 弹窗
      if ([ERROR_CODE.SUB_ACCOUNT_NOT_ALLOW].indexOf(error.code) > -1) {
        popupUtil.create(JoinFailedDialog, {errorCode: error.code});
        return;
      }
      mc.send('toast', 'error', error);
    });
    return function () {
      successSubscription.unsubscribe();
      failSubscription.unsubscribe();
      automaticJoinSubscription.unsubscribe();
      clearSubscription.unsubscribe();
    };
  });

  function automaticJoinIfNecessary() {
    const autoJoin = localStorage.getItem(STORAGE_KEY);
    // 带auto join参数 自动调join接口
    if (autoJoin === 'Y') {
      joinNow$.next();
    }
  }

  function handleClickJoinNow() {
    if ($userInfo$.userId) {
      joinNow$.next();
      return;
    }
    const target = `${location.origin}${$fixLink$(PATHS.CRYPTO_TRADING_COMPETITION_1_ON_1.pathname)}`;
    // 未登陆过 跳转到创建用户页
    if (!hasUsedUID()) {
      goto($fixLink$(PATHS.REGISTER.pathname) + '?' + stringify({target}));
      return;
    }
    // 在移动端，跳转到登陆页
    if (bannerWidth < 640) {
      goto($fixLink$(PATHS.LOGIN.pathname) + '?' + stringify({target}));
      return;
    }
    drawerUtil.launchLogin(LG, target);
    localStorage.setItem(STORAGE_KEY, 'Y');
  }

  function handleClickTrade() {
    if (envUtil.isApp) {
      bridge.postMessage('toContractTrade');
      return;
    }
    window.location.assign(`${LINKS.PHEMEX_CONTRACT_TRADE}/BTCUSD`);
  }
</script>

<style>
  .banner {
    position: relative;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    white-space: nowrap;
  }
  .green {
    color: #37d201;
  }
  .white {
    color: #fff;
  }
  .banner .bg {
    width: 100%;
  }
  .banner-txt {
    font-size: 72px;
  }
  .banner-sub-title {
    border-radius: 12px;
    line-height: 48px;
    font-size: 24px;
  }
  .banner-content {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-around;
  }

  @media screen and (max-width: 640px) {
    .banner {
      width: 100%;
      height: 200px;
      overflow: hidden;
    }
    .banner-content {
      padding-top: 28px;
    }
    .banner .bg {
      width: auto;
      height: 100%;
    }
    .banner-txt {
      font-size: 36px;
    }
    .banner-sub-title {
      border-radius: 6px;
      line-height: 32px;
      font-size: 16px;
    }
  }
</style>

<div class="banner pr" bind:offsetWidth={bannerWidth}>
  <img alt="banner" width="100%" height="auto" class="bg" src={TRADING_GAME.BANNER} />
  <div class="banner-content">
    <div class="tc">
      <div class="banner-txt fw3">
        <span class="green">{LG(lang.BannerTitle1)}</span>
        <span class="white">{LG(lang.BannerTitle2)}</span>
      </div>
      <div class="banner-sub-title dib ph24 fw3 B1 BLUE">{LG(lang.BannerSubTitle1)} <span class="T1">{LG(lang.BannerSubTitle2)}</span> {LG(lang.BannerSubTitle3)}</div>
    </div>
    {#if $shouldDisplayJoinBtn$}
      <Button type="white" minWidth="200px" newRadius on:click={handleClickJoinNow}>
        <span class="fw3 T1">{LG(lang.JoinNow)}</span>
      </Button>
    {/if}
    {#if $myEntryInfo$.isJoinedCompetition}
      <Button type="white" minWidth="200px" newRadius on:click={handleClickTrade}>
        <span class="fw3 T1">{LG(lang.SpotTrade)}</span>
      </Button>
    {/if}
  </div>
</div>
